import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { PageAvailableLanguage, PageMetadataModel, PageModel } from '@libs/cms/models';
import { CmsLanguageService } from '@libs/shared/common';

interface PageMetadataUpdate {
  metadata?: PageMetadataModel;
  hideFrom?: string;
  fallbackTitle?: string;
  availableLanguages?: PageAvailableLanguage[];
}

@Injectable({
  providedIn: 'root',
})
export class PageMetadataService {
  private readonly document: Document = inject(DOCUMENT);
  private readonly cmsLanguageService: CmsLanguageService = inject(CmsLanguageService);
  private readonly meta: Meta = inject(Meta);
  private readonly title: Title = inject(Title);

  public update(page: PageModel, fallbackTitle?: string): void {
    this.updateMetadata({
      metadata: page.metadata,
      hideFrom: page.hideFrom,
      fallbackTitle,
      availableLanguages: page.availableLanguages,
    });
  }

  private updateMetadata(update: PageMetadataUpdate): void {
    const {
      metadata,
      hideFrom,
      fallbackTitle,
      availableLanguages,
    } = update;

    if (metadata?.metaTitle) {
      this.title.setTitle(metadata.metaTitle);
    } else if (fallbackTitle) {
      this.title.setTitle(fallbackTitle);
    }

    this.updateMetaTag('description', metadata?.metaDescription);
    this.updateMetaTag('keywords', metadata?.metaKeywords);
    this.updateMetaTag('robots', metadata?.indexFollow);
    this.updateMetaTag('googlebot', hideFrom
      ? `unavailable_after: ${hideFrom}`
      : undefined);
    this.updateLanguageLinks(availableLanguages);
  }

  private updateMetaTag(name: string, content?: string | string[]): void {
    const value: string | undefined = Array.isArray(content)
      ? content.join(', ')
      : content;

    if (value) {
      this.meta.updateTag({
        name,
        content: value,
      });

      return;
    }

    this.meta.removeTag(`name="${name}"`);
  }

  private updateLanguageLinks(availableLanguages?: PageAvailableLanguage[]): void {
    this.document.head
      .querySelectorAll<HTMLLinkElement>('link[rel="alternate"][hreflang]')
      .forEach((link: HTMLLinkElement) => {
        link.remove();
      });

    const currentLanguage: string | undefined = this.cmsLanguageService.language();

    if (currentLanguage) {
      // Keep html[lang] aligned with the language the CMS served
      this.document.documentElement.lang = currentLanguage;
    }

    availableLanguages?.forEach((item: PageAvailableLanguage) => {
      this.addAlternateLanguageLink(item.language, item.path);
    });
  }

  private addAlternateLanguageLink(hreflang: string, path: string): void {
    const link: HTMLLinkElement = this.document.createElement('link');

    link.rel = 'alternate';
    link.hreflang = hreflang;
    link.href = new URL(path, this.document.location.origin).href;

    this.document.head.appendChild(link);
  }
}
